<template>
    <PageLayoutForms>
        <template #header>
            <h1 class="pdp-mandate-onboarding-legal-representative__title typographies-heading-h1">
                {{ $t.__('pdp.mandate.Onboarding.title') }}
            </h1>
        </template>
        <template #content>
            <CompassInformationBanner
                :title="$t.__('pdp.mandate.OnboardingLegalRepresentative.InformationBanner.title')"
                sourceIllustration="/ressources/logged/img/illustrations/pdp/invoicing-signature.svg"
            >
                <template #description>
                    <div
                        class="pdp-mandate-onboarding-legal-representative__information typographies-paragraph-medium-regular"
                        v-html="informationBannerDescription"
                    />
                </template>
            </CompassInformationBanner>
            <h2
                class="pdp-mandate-onboarding-legal-representative__subtitle typographies-heading-h2"
            >
                {{ $t.__('pdp.mandate.OnboardingLegalRepresentative.subtitle') }}
            </h2>
            <div class="pdp-mandate-onboarding-legal-representative__form">
                <CompassFormItem
                    :label="$t.__('pdp.mandate.OnboardingLegalRepresentative.formItem.email')"
                    size="large"
                    :error="
                        errors.email
                            ? $t.__('common.labels.please_enter_a_valid_email_address')
                            : ''
                    "
                >
                    <CompassInput
                        v-model="email"
                        type="email"
                        size="large"
                        :error="errors.email"
                        @blur="errors.email = !!email ? !validateEmail(email) : false"
                        @input="handleEmailInput"
                    />
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('pdp.mandate.OnboardingLegalRepresentative.formItem.mobile')"
                    size="large"
                    :error="
                        errors.mobile
                            ? $t.__('common.labels.please_enter_a_valid_phone_number')
                            : ''
                    "
                >
                    <CompassTelInput
                        :value="mobile"
                        size="large"
                        defaultCountry="fr"
                        :error="errors.mobile"
                        @input="errors.mobile = false"
                        @blur="($event) => handleTelInputBlur($event)"
                        @validate="(value) => updatePhoneNumber(value)"
                        @country-changed="handleCountryChange($event)"
                    />
                </CompassFormItem>
            </div>
            <div class="pdp-mandate-onboarding-legal-representative__help">
                <img
                    src="/ressources/logged/img/illustrations/pdp/shield-check.svg"
                    alt="Information"
                    height="20"
                    width="20"
                />
                <div
                    class="pdp-mandate-onboarding-legal-representative__help-content typographies-paragraph-medium-regular"
                >
                    <p>
                        {{ $t.__('pdp.mandate.OnboardingLegalRepresentative.helpText.1') }}
                    </p>
                    <p
                        v-html="
                            $t.__('pdp.mandate.OnboardingLegalRepresentative.helpText.2', {
                                linkStart:
                                    '<a href=\'https:\/\/go.sellsy.com\/informations-legales\/confidentialite-des-donnees\' target=\'_blank\' rel=\'noopener noreferrer\'>',
                                linkEnd: '</a>',
                            })
                        "
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <FooterFullPage :primaryAction="primaryAction" :secondaryActions="secondaryActions" />
        </template>
    </PageLayoutForms>
</template>
<script>
import FooterFullPage from 'vcomponents/common/FooterFullPage/FooterFullPage.vue';
import { validateEmail } from 'Helpers/validator/EmailValidator';
import { validatePhoneNumber } from 'Helpers/validator/PhoneNumberValidator';
import PageLayoutForms from 'vcomponents/common/layout/pages/PageLayoutForms.vue';
import { createMandate } from 'api/mandate';
import { goToUrl } from 'vueDir/helpers.js';

export default {
    components: {
        FooterFullPage,
        PageLayoutForms,
    },
    props: {
        legalRepresentative: {
            type: Object,
            required: true,
        },
        selectedMandateType: {
            type: Array,
            required: true,
        },
        company: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            primaryAction: {
                label: this.$t.__('pdp.mandate.OnboardingLegalRepresentative.finish_button'),
                disabled: true,
                loading: false,
                action: this.handleSubmit,
            },
            secondaryActions: [
                {
                    label: this.$t.__('common.actions.cancel'),
                    action: () => {
                        this.$router.push({ name: 'dashboard' });
                    },
                },
            ],
            informationBannerDescription: this.$t.__(
                'pdp.mandate.OnboardingLegalRepresentative.InformationBanner.description',
                {
                    paragraphStart: '<p>',
                    paragraphEnd: '</p>',
                    listStart: '<ul>',
                    listEnd: '</ul>',
                    listItemStart: '<li>',
                    listItemEnd: '</li>',
                    boldStart: '<strong>',
                    boldEnd: '</strong>',
                },
            ),
            email: '',
            mobile: '',
            country: {},
            errors: {
                email: false,
                mobile: false,
            },
        };
    },
    methods: {
        validateEmail,
        validatePhoneNumber,
        updateSubmitDisabled() {
            if (this.mobile && this.validateEmail(this.email)) {
                this.primaryAction.disabled = false;
            } else {
                this.primaryAction.disabled = true;
            }
        },
        handleEmailInput() {
            this.errors.email = false;
            this.updateSubmitDisabled();
        },
        handleCountryChange($event) {
            this.country = $event;
        },
        updatePhoneNumber(value) {
            this.mobile = value.valid ? value.formatted : '';
            this.updateSubmitDisabled();
        },
        handleTelInputBlur($event) {
            if (!$event || $event.target.closest('.compass-select-flag')) {
                this.errors.mobile = false;
                return;
            }

            const inputValue = $event.target.value;
            if (!inputValue || inputValue.replace('+', '') === this.country.dialCode) {
                this.errors.mobile = false;
                return;
            }

            this.errors.mobile = !this.validatePhoneNumber(this.mobile);
        },
        handleSubmit() {
            this.primaryAction.loading = true;

            if (window.Intercom) {
                window.Intercom('trackEvent', 'Flow PA > Step 4 Continue Clicked', {
                    mandateType: this.selectedMandateType,
                });
            }

            if (window.pendo?.accountId) {
                window.pendo.track('Flow PA > Step 4 Continue Clicked', {
                    mandateType: this.selectedMandateType,
                });
            }

            axios
                .get(`/datagouv/company/${this.company.legal_france.siren}`)
                .then((response) => {
                    const headquarters = response.data[0]?.siege;

                    const payload = {
                        types: this.selectedMandateType,
                        company_name: this.company.legal_name,
                        company_siren: this.company.legal_france.siren,
                        company_siret: this.company.legal_france.siret,
                        company_address: {
                            name: 'Headquarters',
                            address_line_1: `${headquarters?.numero_voie} ${headquarters?.type_voie} ${headquarters?.libelle_voie}`,
                            address_line_2: headquarters?.complement_adresse,
                            postal_code: headquarters?.code_postal,
                            city: headquarters?.libelle_commune,
                            country_code: this.company.country_code || 'FR',
                        },
                        legal_representative_first_name: this.legalRepresentative.firstName,
                        legal_representative_last_name: this.legalRepresentative.lastName,
                        legal_representative_birthdate: this.legalRepresentative.birthDate,
                        legal_representative_email: this.email,
                        legal_representative_mobile_number: this.mobile.replace(/\s/g, ''),
                        legal_form: this.company.legal_france.company_type || '',
                        person_type: 'PRIVATE_PERSON',
                        tax_id: this.company.legal_france.vat || '',
                        capital: this.company.capital || '',
                    };
                    if (this.selectedMandateType.includes('receipt')) {
                        payload.electronic_address = this.company.electronicAddress;
                    }

                    createMandate(payload)
                        .then(() => {
                            goToUrl('/settings/pa/mandate');
                        })
                        .catch(() => {
                            this.$pushToast({
                                type: 'error',
                                text: this.$t.__(
                                    'pdp.mandate.OnboardingLegalRepresentative.error_creating_mandate',
                                ),
                            });
                            this.primaryAction.loading = false;
                        });
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        text: this.$t.__(
                            'pdp.mandate.OnboardingLegalRepresentative.error_fetching_company',
                        ),
                    });
                    this.primaryAction.loading = false;
                });
        },
    },
};
</script>
<style lang="scss">
body:has(.pdp-mandate-onboarding-legal-representative) {
    #sellsy_app {
        background-color: var(--colors-common-background-default);
    }
}
</style>
<style lang="scss" scoped>
.pdp-mandate-onboarding-legal-representative {
    &::v-deep {
        .information-banner {
            margin-bottom: var(--spacing-4);
        }
    }

    &__information {
        color: var(--colors-common-text-brand-secondary-blue);

        &::v-deep {
            p {
                margin: 0;

                & + ul {
                    list-style: revert;
                    margin: var(--spacing-3) 0 0;
                    padding: revert;
                }
            }
        }
    }

    &__form {
        display: flex;
        gap: var(--spacing-4);

        &::v-deep {
            .compass-form-item {
                flex: 1;
            }

            .compass-tel-input .compass-input {
                flex: 1;
            }
        }
    }

    &__help {
        border-top: 1px solid var(--colors-common-border-default);
        display: flex;
        gap: var(--spacing-1);
        margin: var(--spacing-5) 0 0;
        padding: var(--spacing-5) 0 0;

        &-content {
            align-items: start;
            color: var(--colors-common-text-standard-default);
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);

            &::v-deep {
                a {
                    color: var(--colors-common-text-standard-default);
                    text-decoration: underline;

                    &:hover {
                        text-decoration: none;
                    }
                }
            }

            p {
                margin: 0;
            }
        }
    }
}
</style>
